/** Rights administration — the permission matrix.
 *
 * Everything else in this application enforces rights. This is the one
 * screen that grants them.
 *
 * `RIGHTS_MODEL.md` §2 on what GHERP got wrong:
 *
 *     449 screens x 12 flags = 5,388 individual settings per user. A mistake
 *     is invisible until someone is blocked or over-privileged. In practice
 *     systems like this get administered by copying an existing user, which
 *     silently propagates whatever was wrong with that user.
 *
 * The matrix below is roles x named permissions instead — about 35 rows and
 * 7 columns, once per role, shared by everyone holding it. A person is
 * granted a *job*, not five thousand boxes.
 */

import { useCallback, useEffect, useMemo, useState } from 'react'
import { api, ApiError } from '@/lib/api'
import { SearchSelect } from '@/components/SearchSelect'
import { useRights } from '@/lib/rights'

interface CatalogueObject {
  module: string
  object: string
  permissions: Record<string, string>   // action -> permission_id
}
interface Catalogue { objects: CatalogueObject[]; actions: string[] }

interface Role { role_id: string; name: string; description?: string }
interface UserRole { role_id: string; name: string; held: boolean }
interface Authority {
  doc_type: string; level: number
  min_value: number | null; max_value: number | null; currency: string
}

const ACTION_LABEL: Record<string, string> = {
  view: 'View', add: 'Add', modify: 'Modify', delete: 'Delete',
  print: 'Print / Export', approve: 'Approve', view_values: 'See Values',
  // Lifecycle actions that are deliberately NOT ordinary edits. Closing a
  // financial year states that a period is final; terminating a session ends
  // somebody's working day. Each has its own permission precisely so it
  // cannot be reached through `modify`.
  close: 'Close', reopen: 'Reopen', terminate: 'Terminate',
}

/** The two that carry real weight get called out rather than sitting in a
 *  row of identical checkboxes. LEGACY_ANALYSIS.md §6.1 found `Mask` is the
 *  most-used right in the whole legacy system, and Authorise is what stops
 *  material being ordered without approval. */
const WEIGHTY = new Set(['approve', 'view_values'])

export function RightsScreen() {
  const [tab, setTab] = useState<'roles' | 'people'>('roles')
  return (
    <>
      <h1 className="page-title">Rights</h1>
      <p className="page-subtitle">
        Roles carry permissions; people carry exceptions.
      </p>

      <div className="tabs">
        <button className={tab === 'roles' ? 'tab active' : 'tab'}
                onClick={() => setTab('roles')}>Roles &amp; Permissions</button>
        <button className={tab === 'people' ? 'tab active' : 'tab'}
                onClick={() => setTab('people')}>People</button>
      </div>

      {tab === 'roles' ? <RoleMatrix /> : <PeoplePanel />}
    </>
  )
}

// ---------------------------------------------------------------------------

function RoleMatrix() {
  const [catalogue, setCatalogue] = useState<Catalogue | null>(null)
  const [roles, setRoles] = useState<Role[]>([])
  const [roleId, setRoleId] = useState('')
  const [held, setHeld] = useState<Set<string>>(new Set())
  const [dirty, setDirty] = useState(false)
  // admin.role.modify - what rights_admin checks to change a role. Without it
  // the matrix is read, not offered for change. RIGHTS_MODEL.md 6.1.
  const rights = useRights()
  const canEdit = rights.can('admin.role.modify')
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  useEffect(() => {
    void (async () => {
      try {
        const [cat, rs] = await Promise.all([
          api.get<Catalogue>('/admin/permissions'),
          api.get<{ rows: Role[] }>('/admin/roles'),
        ])
        setCatalogue(cat); setRoles(rs.rows)
        if (rs.rows.length) setRoleId(rs.rows[0].role_id)
      } catch (e) {
        setError(e instanceof ApiError ? e.message : 'Could not load rights.')
      }
    })()
  }, [])

  const loadRole = useCallback(async (id: string) => {
    if (!id) return
    try {
      const r = await api.get<{ held: string[] }>(
        `/admin/roles/${id}/permissions`)
      setHeld(new Set(r.held)); setDirty(false)
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Could not load the role.')
    }
  }, [])

  useEffect(() => { void loadRole(roleId) }, [roleId, loadRole])

  function toggle(permissionId?: string) {
    if (!permissionId) return
    setHeld(prev => {
      const next = new Set(prev)
      next.has(permissionId) ? next.delete(permissionId) : next.add(permissionId)
      return next
    })
    setDirty(true)
  }

  function toggleRow(obj: CatalogueObject, on: boolean) {
    setHeld(prev => {
      const next = new Set(prev)
      for (const id of Object.values(obj.permissions)) {
        on ? next.add(id) : next.delete(id)
      }
      return next
    })
    setDirty(true)
  }

  async function save() {
    setBusy(true); setError(null)
    try {
      await api.update(`/admin/roles/${roleId}/permissions`,
                       { permission_ids: [...held] })
      setDirty(false)
      rights.refresh()     // the change may be to a role this person holds
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'The change was not saved.')
    } finally { setBusy(false) }
  }

  const byModule = useMemo(() => {
    const groups: Record<string, CatalogueObject[]> = {}
    for (const o of catalogue?.objects ?? []) {
      (groups[o.module] ??= []).push(o)
    }
    return groups
  }, [catalogue])

  if (error && !catalogue) return <div className="card"><div className="error-state">{error}</div></div>
  if (!catalogue) return <div className="card"><div className="loading">Loading…</div></div>

  return (
    <div className="card">
      <div className="toolbar">
        <div style={{ minWidth: 220 }}>
          <SearchSelect
            id="rights-role"
            value={roleId}
            emptyLabel="— choose a role —"
            onChange={setRoleId}
            options={roles.map(r => ({ value: r.role_id, label: r.name }))}
          />
        </div>
        <span className="form-help">{held.size} permissions held</span>
        <div className="grow" />
        {dirty && <span className="unsaved">Unsaved changes</span>}
        {canEdit ? (
          <button className="btn btn-primary" disabled={!dirty || busy}
                  onClick={() => void save()}>
            {busy ? 'Saving…' : 'Save'}
          </button>
        ) : (
          <span className="form-help">You can see these permissions but not change them.</span>
        )}
      </div>

      {error && <div className="form-errors" role="alert">{error}</div>}

      <div style={{ overflowX: 'auto' }}>
        <table className="matrix">
          <thead>
            <tr>
              <th style={{ minWidth: 200 }}>Screen</th>
              {catalogue.actions.map(a => (
                <th key={a} className={WEIGHTY.has(a) ? 'num weighty' : 'num'}>
                  {ACTION_LABEL[a] ?? a}
                </th>
              ))}
              <th style={{ width: 60 }} />
            </tr>
          </thead>
          <tbody>
            {Object.entries(byModule).map(([module, objects]) => (
              <>
                <tr key={module} className="matrix-group">
                  <td colSpan={catalogue.actions.length + 2}>{module}</td>
                </tr>
                {objects.map(o => {
                  const ids = Object.values(o.permissions)
                  const all = ids.length > 0 && ids.every(id => held.has(id))
                  return (
                    <tr key={`${o.module}.${o.object}`}>
                      <td>{o.object.replace(/_/g, ' ')}</td>
                      {catalogue.actions.map(a => {
                        const id = o.permissions[a]
                        return (
                          <td key={a} className="num">
                            {id ? (
                              <input
                                type="checkbox"
                                checked={held.has(id)}
                                disabled={!canEdit}
                                onChange={() => toggle(id)}
                                aria-label={`${o.module}.${o.object} ${a}`}
                              />
                            ) : <span className="na">—</span>}
                          </td>
                        )
                      })}
                      <td className="num">
                        {canEdit && (
                          <button className="link-btn"
                                  onClick={() => toggleRow(o, !all)}>
                            {all ? 'none' : 'all'}
                          </button>
                        )}
                      </td>
                    </tr>
                  )
                })}
              </>
            ))}
          </tbody>
        </table>
      </div>

      <p className="form-note">
        A blank cell means the action does not exist for that screen — not
        that it is denied. <strong>Everything starts denied</strong>: absence
        of a permission is refusal, so a new screen is closed to everyone
        until it is granted here.
      </p>
    </div>
  )
}

// ---------------------------------------------------------------------------

function PeoplePanel() {
  const [users, setUsers] = useState<{ user_id: string; name: string }[]>([])
  const [userId, setUserId] = useState('')
  const [roles, setRoles] = useState<UserRole[]>([])
  const [authority, setAuthority] = useState<Authority[]>([])
  const [effective, setEffective] = useState<string[]>([])
  // admin.user.modify - what rights_admin checks to change a person's roles.
  const rights = useRights()
  const canEdit = rights.can('admin.user.modify')
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  useEffect(() => {
    void (async () => {
      try {
        const r = await api.get<{ rows: { user_id: string; name: string }[] }>(
          '/admin/users')
        setUsers(r.rows)
        if (r.rows.length) setUserId(r.rows[0].user_id)
      } catch (e) {
        setError(e instanceof ApiError ? e.message : 'Could not load people.')
      }
    })()
  }, [])

  const load = useCallback(async (id: string) => {
    if (!id) return
    try {
      const r = await api.get<{ roles: UserRole[]; authority: Authority[]
                                effective: string[] }>(
        `/admin/users/${id}/rights`)
      setRoles(r.roles); setAuthority(r.authority); setEffective(r.effective)
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Could not load rights.')
    }
  }, [])

  useEffect(() => { void load(userId) }, [userId, load])

  async function saveRoles(next: UserRole[]) {
    setBusy(true); setError(null)
    try {
      await api.update(`/admin/users/${userId}/roles`,
                       { role_ids: next.filter(r => r.held).map(r => r.role_id) })
      await load(userId)
      rights.refresh()     // it may have been this person's own roles
    } catch (e) {
      // The lockout guard speaks here: "this would leave nobody able to
      // administer rights in this company."
      setError(e instanceof ApiError ? e.message : 'The change was not saved.')
      await load(userId)
    } finally { setBusy(false) }
  }

  return (
    <div className="card">
      <div className="toolbar">
        <div style={{ minWidth: 240 }}>
          <SearchSelect
            id="rights-user"
            value={userId}
            emptyLabel="— choose a person —"
            onChange={setUserId}
            options={users.map(u => ({ value: u.user_id, label: u.name }))}
          />
        </div>
        <div className="grow" />
        <span className="form-help">
          {effective.length} effective permissions
        </span>
      </div>

      {error && <div className="form-errors" role="alert">{error}</div>}

      <div className="form-grid">
        <div>
          <div className="card-header" style={{ border: 0, padding: '0 0 8px' }}>
            Roles
          </div>
          {roles.length === 0 && (
            <p className="form-help">No roles defined in this company yet.</p>
          )}
          {!canEdit && roles.length > 0 && (
            <p className="form-help">You can see these roles but not change them.</p>
          )}
          {roles.map(r => (
            <label key={r.role_id} className="check-row">
              <input type="checkbox" checked={r.held} disabled={busy || !canEdit}
                     onChange={() => {
                       const next = roles.map(x => x.role_id === r.role_id
                         ? { ...x, held: !x.held } : x)
                       setRoles(next); void saveRoles(next)
                     }} />
              <span>{r.name}</span>
            </label>
          ))}
        </div>

        <div>
          <div className="card-header" style={{ border: 0, padding: '0 0 8px' }}>
            Approval authority
          </div>
          {authority.length === 0 ? (
            <p className="form-help">
              None. This person cannot approve anything, whatever their roles
              say — approving needs both the permission and an authority.
            </p>
          ) : (
            <table>
              <thead><tr>
                <th>Document</th><th>Level</th><th className="num">Up to</th>
              </tr></thead>
              <tbody>
                {authority.map((a, i) => (
                  <tr key={i}>
                    <td>{a.doc_type}</td>
                    <td>{a.level}</td>
                    <td className="num">
                      {a.max_value === null
                        ? <span className="unbounded">no limit</span>
                        : Number(a.max_value).toLocaleString('en-IN')}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          <p className="form-note" style={{ padding: '8px 0 0' }}>
            GHERP recorded <strong>no approval limits at all</strong> — anyone
            holding the Authorise flag could approve any amount. “No limit”
            here means the same thing, and is shown so it can be seen and
            changed rather than assumed.
          </p>
        </div>
      </div>
    </div>
  )
}
